import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { Label } from "./annotate";

/**
 * Anivaze v4 — NEURAL NETWORK actor. "Neural networks / machine learning" is one
 * of the most-requested topics and it was rendering as 3 boxes on a dotted rail.
 * This draws the REAL canonical figures, animated as a pure function of `t`:
 *
 *   neuron   — a single artificial neuron: inputs × weights → Σ → activation → out,
 *              with signals flowing through it.
 *   network  — a real multi-layer perceptron (input · hidden · hidden · output),
 *              fully connected, with a FORWARD-PASS wave sweeping left→right.
 *   backprop — the training LOOP: forward pass (blue) → error at the output (red)
 *              → gradient flowing BACKWARD (right→left) → weights nudge → repeat,
 *              with the loss ticking down. The looping mechanism, not a straight line.
 *
 * Director drives it via items[0] = subject (parsed from the beat text).
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (x: number) => { const c = clamp01(x); return c * c * (3 - 2 * c); };
const rnd = (i: number, s = 1) => { const x = Math.sin((i + 1) * 12.9898 * s + 78.233) * 43758.5453; return x - Math.floor(x); };

export type NeuralSubject = "neuron" | "network" | "backprop";
export interface NeuralConfig { subject: NeuralSubject }

export function parseNeuralConfig(text: string): NeuralConfig {
  const s = text.toLowerCase();
  // Order matters: the TRAINING LOOP wins first; then anything that stacks
  // neurons INTO LAYERS is the whole network; a lone neuron only otherwise.
  const subject: NeuralSubject =
    /backprop|back-?propagat|gradient descent|learns? by fail|adjust(s|ing)? (the )?weights|training loop|minimi[sz]e|reduce(s)? (the )?error|loss function|nudge(s)? (the )?weights|makes? a prediction/.test(s) ? "backprop"
    : /\blayers?\b|\bstack|\bnetwork\b|architecture|deep learning|input layer|output layer|hidden layer|forward pass|from data|many neurons|multiple neurons/.test(s) ? "network"
    : /\bneurons?\b|perceptron|takes in numbers|multiplies|weighted sum|activation|threshold|fires when|dot product/.test(s) ? "neuron"
    : "network";
  return { subject };
}

/** Neural-net / ML signal — route these beats to the real network, not a rail. */
// MECHANISM signals only — a beat must be about how the network WORKS (neurons,
// layers, weights, activation, forward pass, backprop, training), not merely
// mention the field "machine learning"/"AI". Broader/applied ML beats stay open
// to a contextual image or another visual, so the video isn't one repeated diagram.
export const NEURAL_RE =
  /neural network|\bneuron|perceptron|hidden layer|input layer|output layer|activation function|backprop|back-?propagat|gradient descent|weights? and bias|\bmlp\b|forward pass|train(s|ing)? (a |an |the )?(model|network|neural)|stack(ing)? (neurons|layers)|(adjust|nudge|tweak)(s|ing)? (the )?weights|reduce(s)? the error|measure(s)? (how wrong|the error)|loss function/i;

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

// ── shared: build layered node positions inside a box ────────────────────────
function layout(box: Box, sizes: number[], u: number) {
  const padX = 8 * u, padY = 6 * u;
  const x0 = box.x + padX, x1 = box.x + box.w - padX;
  const cols = sizes.length;
  return sizes.map((n, li) => {
    const x = cols === 1 ? box.cx : x0 + ((x1 - x0) * li) / (cols - 1);
    const h = box.h - 2 * padY;
    return Array.from({ length: n }, (_, i) => ({
      x, y: box.y + padY + (n === 1 ? h / 2 : (h * i) / (n - 1)),
    }));
  });
}

const NodeCircle: React.FC<{ x: number; y: number; r: number; act: number; theme: Theme }> = ({ x, y, r, act, theme }) => (
  <>
    <circle cx={x} cy={y} r={r * (1 + 0.18 * act)} fill={theme.palette.primary} opacity={0.12 + 0.2 * act} />
    <circle cx={x} cy={y} r={r} fill={theme.dark ? "#1b2330" : "#eef2f7"} stroke={theme.palette.primary}
      strokeWidth={Math.max(1, r * 0.16)} />
    <circle cx={x} cy={y} r={r * 0.5} fill={theme.palette.primary} opacity={0.35 + 0.6 * act} />
  </>
);

// ── NETWORK — multilayer perceptron with a forward-pass wave ─────────────────
const NetworkMLP: React.FC<VProps & { u: number; sizes?: number[]; labels?: boolean }> = ({ box, theme, t, u, sizes = [4, 6, 5, 3], labels = true }) => {
  const P = theme.palette;
  const cols = layout(box, sizes, u);
  const r = Math.min(u * 1.5, (box.h / (Math.max(...sizes) * 2.4)));
  const T = 2.8;                                  // seconds per forward sweep
  const front = ((t / T) % 1) * (sizes.length + 0.4);
  const nodeAct = (li: number) => clamp01(1 - Math.abs(front - li) * 1.3);
  const edgeAct = (li: number) => clamp01(1 - Math.abs(front - (li + 0.5)) * 1.6);
  const names = ["Input", sizes.length > 3 ? "Hidden" : "Hidden", "Hidden", "Output"];
  return (
    <g>
      {/* edges (fully connected between adjacent layers) */}
      {cols.slice(0, -1).map((col, li) =>
        col.map((a, ai) =>
          cols[li + 1].map((b, bi) => {
            const w = 0.25 + 0.75 * rnd(li * 97 + ai * 13 + bi, 3); // deterministic "weight"
            const ea = edgeAct(li);
            return (
              <line key={`e${li}-${ai}-${bi}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={ea > 0.05 ? P.primary : P.muted}
                strokeWidth={Math.max(0.5, u * 0.12 * w)}
                strokeOpacity={0.08 + 0.55 * ea * w} />
            );
          }),
        ),
      )}
      {/* travelling signal dots on the firing layer's edges (a few, for life) */}
      {cols.slice(0, -1).map((col, li) => {
        const ea = edgeAct(li);
        if (ea < 0.15) return null;
        const f = clamp01(front - li);
        return col.slice(0, 3).map((a, ai) => {
          const b = cols[li + 1][ai % cols[li + 1].length];
          return <circle key={`d${li}-${ai}`} cx={a.x + (b.x - a.x) * f} cy={a.y + (b.y - a.y) * f}
            r={u * 0.55} fill={P.highlight} opacity={ea} />;
        });
      })}
      {/* neurons */}
      {cols.map((col, li) => col.map((nd, i) => (
        <NodeCircle key={`n${li}-${i}`} x={nd.x} y={nd.y} r={r} act={nodeAct(li)} theme={theme} />
      )))}
      {/* layer labels */}
      {labels && cols.map((col, li) => {
        const nm = li === 0 ? "Input" : li === cols.length - 1 ? "Output" : "Hidden";
        return <text key={`l${li}`} x={col[0].x} y={box.y + box.h - u * 1.5} textAnchor="middle"
          fontFamily={theme.body.family} fontWeight={700} fontSize={u * 1.7} fill={P.sub}>{nm}</text>;
      })}
    </g>
  );
};

// ── NEURON — inputs × weights → Σ → activation → output ──────────────────────
const Neuron: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const cy = box.cy;
  const inX = box.x + 9 * u;
  const sumX = box.cx - 2 * u;
  const actX = box.cx + 7 * u;
  const outX = box.x + box.w - 8 * u;
  const ins = [-1, 0, 1].map((k, i) => ({ x: inX, y: cy + k * 8 * u, w: 0.4 + 0.5 * rnd(i, 5), label: `x${i + 1}` }));
  const phase = (t % 2.2) / 2.2;                 // one fire every 2.2s
  const toSum = smooth(phase / 0.5);
  const toOut = smooth((phase - 0.55) / 0.4);
  const fire = clamp01((phase - 0.5) / 0.1) * (1 - clamp01((phase - 0.95) / 0.05));
  return (
    <g>
      {/* input → sum edges with weights */}
      {ins.map((nd, i) => (
        <g key={i}>
          <line x1={nd.x} y1={nd.y} x2={sumX} y2={cy} stroke={P.primary} strokeWidth={u * 0.15 + u * 0.35 * nd.w} strokeOpacity={0.5} />
          <circle cx={nd.x} cy={nd.y} r={u * 2} fill={theme.dark ? "#1b2330" : "#eef2f7"} stroke={P.primary} strokeWidth={u * 0.3} />
          <text x={nd.x} y={nd.y + u * 0.6} textAnchor="middle" fontFamily={theme.body.family} fontWeight={700} fontSize={u * 1.6} fill={P.ink}>{nd.label}</text>
          <text x={(nd.x + sumX) / 2} y={(nd.y + cy) / 2 - u * 0.8} textAnchor="middle" fontFamily={theme.body.family} fontSize={u * 1.4} fill={P.sub}>w{i + 1}</text>
          {/* signal dot travelling in */}
          <circle cx={nd.x + (sumX - nd.x) * toSum} cy={nd.y + (cy - nd.y) * toSum} r={u * 0.6} fill={P.highlight} opacity={toSum < 1 ? 1 : 0} />
        </g>
      ))}
      {/* sum node */}
      <circle cx={sumX} cy={cy} r={u * 3.2 * (1 + 0.12 * fire)} fill={theme.dark ? "#1b2330" : "#eef2f7"} stroke={P.primary} strokeWidth={u * 0.35} />
      <text x={sumX} y={cy + u} textAnchor="middle" fontFamily={theme.body.family} fontWeight={800} fontSize={u * 3} fill={P.primary}>Σ</text>
      {/* activation box */}
      <rect x={actX - 2.6 * u} y={cy - 2.6 * u} width={5.2 * u} height={5.2 * u} rx={u * 0.6} fill={theme.dark ? "#1b2330" : "#eef2f7"} stroke={P.secondary} strokeWidth={u * 0.3} />
      <path d={`M ${actX - 2 * u} ${cy + 1.6 * u} L ${actX - 0.2 * u} ${cy + 1.6 * u} L ${actX - 0.2 * u} ${cy - 1.6 * u} L ${actX + 2 * u} ${cy - 1.6 * u}`}
        fill="none" stroke={P.secondary} strokeWidth={u * 0.35} />
      <line x1={sumX + 3.2 * u} y1={cy} x2={actX - 2.6 * u} y2={cy} stroke={P.primary} strokeWidth={u * 0.3} />
      {/* output */}
      <line x1={actX + 2.6 * u} y1={cy} x2={outX - 2 * u} y2={cy} stroke={P.primary} strokeWidth={u * 0.3} />
      <circle cx={outX} cy={cy} r={u * 2.2 * (1 + 0.15 * fire)} fill={P.primary} opacity={0.25 + 0.6 * fire} />
      <circle cx={outX} cy={cy} r={u * 2.2} fill="none" stroke={P.primary} strokeWidth={u * 0.3} />
      <text x={outX} y={cy + u * 0.6} textAnchor="middle" fontFamily={theme.body.family} fontWeight={800} fontSize={u * 1.7} fill={P.ink}>y</text>
      {/* output-firing dot */}
      <circle cx={actX + 2.6 * u + (outX - 2 * u - (actX + 2.6 * u)) * toOut} cy={cy} r={u * 0.6} fill={P.highlight} opacity={toOut > 0 && toOut < 1 ? 1 : 0} />
      <Label tx={inX} ty={cy - 8 * u} lx={inX} ly={box.y + 5 * u} text="inputs × weights" theme={theme} u={u} anchor="middle" />
      <Label tx={sumX} ty={cy + 3.2 * u} lx={sumX} ly={box.y + box.h - 3 * u} text="weighted sum" theme={theme} u={u} anchor="middle" />
      <Label tx={actX} ty={cy - 2.6 * u} lx={actX} ly={box.y + 5 * u} text="activation" theme={theme} u={u} anchor="middle" />
    </g>
  );
};

// ── BACKPROP — the training LOOP: forward → error → backward → repeat ─────────
const Backprop: React.FC<VProps & { u: number }> = ({ box, theme, t, u }) => {
  const P = theme.palette;
  const sizes = [3, 4, 2];
  const inner: Box = { ...box, y: box.y + 3 * u, h: box.h - 12 * u, cy: box.y + 3 * u + (box.h - 12 * u) / 2 };
  const cols = layout(inner, sizes, u);
  const r = u * 1.6;
  const T = 4.5;                                 // one full train step every 4.5s
  const ph = (t % T) / T;                        // 0..1 : forward | error | backward | settle
  const fwd = ph < 0.35, err = ph >= 0.35 && ph < 0.5, back = ph >= 0.5 && ph < 0.85;
  const front = fwd ? (ph / 0.35) * (sizes.length - 1) : back ? (sizes.length - 1) * (1 - (ph - 0.5) / 0.35) : (ph < 0.5 ? sizes.length - 1 : 0);
  const dir = back ? "back" : "fwd";
  // loss ticks down each loop (deterministic per loop index)
  const loop = Math.floor(t / T);
  const loss = Math.max(0.03, 0.6 * Math.pow(0.82, loop) * (1 - 0.25 * ph));
  const col = dir === "back" ? P.danger : P.primary;
  return (
    <g>
      {/* edges — flicker/thicken during the backward pass (weights updating) */}
      {cols.slice(0, -1).map((c, li) =>
        c.map((a, ai) => cols[li + 1].map((b, bi) => {
          const near = clamp01(1 - Math.abs(front - (li + 0.5)) * 1.8);
          const updating = back && near > 0.1;
          return <line key={`e${li}-${ai}-${bi}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke={updating ? P.danger : P.primary} strokeWidth={u * 0.12 * (updating ? 2.2 : 1)}
            strokeOpacity={0.1 + 0.5 * near} />;
        })),
      )}
      {/* signal front */}
      {cols.map((c, li) => c.map((nd, i) => {
        const act = clamp01(1 - Math.abs(front - li) * 1.5);
        return <g key={`n${li}-${i}`}>
          <circle cx={nd.x} cy={nd.y} r={r * (1 + 0.2 * act)} fill={col} opacity={0.12 + 0.25 * act} />
          <circle cx={nd.x} cy={nd.y} r={r} fill={theme.dark ? "#1b2330" : "#eef2f7"} stroke={col} strokeWidth={u * 0.2} />
        </g>;
      }))}
      {/* error burst at the output during the compare phase */}
      {err && cols[cols.length - 1].map((nd, i) => (
        <circle key={`err${i}`} cx={nd.x} cy={nd.y} r={r * 2.4} fill="none" stroke={P.danger} strokeWidth={u * 0.4} opacity={0.8} />
      ))}
      {/* the LOOP arrow: output → back to input */}
      {(() => {
        const ax = cols[cols.length - 1][0].x, bx = cols[0][0].x;
        const yy = box.y + box.h - 6.5 * u;
        const midY = yy + 3.5 * u;
        return <g opacity={back ? 1 : 0.4}>
          <path d={`M ${ax} ${yy} C ${ax} ${midY}, ${bx} ${midY}, ${bx} ${yy}`} fill="none"
            stroke={P.danger} strokeWidth={u * 0.35} strokeDasharray={`${u} ${u}`} />
          <path d={`M ${bx} ${yy} l ${1.2 * u} ${1 * u} M ${bx} ${yy} l ${-1.2 * u} ${1 * u}`} stroke={P.danger} strokeWidth={u * 0.35} />
          <text x={(ax + bx) / 2} y={midY + u * 1.6} textAnchor="middle" fontFamily={theme.body.family} fontWeight={700} fontSize={u * 1.7} fill={P.danger}>adjust weights · repeat</text>
        </g>;
      })()}
      {/* phase caption + loss meter */}
      <text x={box.cx} y={box.y + 2 * u} textAnchor="middle" fontFamily={theme.body.family} fontWeight={800} fontSize={u * 1.9} fill={col}>
        {fwd ? "forward pass →" : err ? "measure error" : back ? "← backpropagate" : "repeat"}
      </text>
      <text x={box.x + box.w - 3 * u} y={box.y + 2 * u} textAnchor="end" fontFamily={theme.body.family} fontWeight={800} fontSize={u * 1.8} fill={P.positive}>
        loss {loss.toFixed(2)} ↓
      </text>
    </g>
  );
};

export const NeuralActor: React.FC<VProps> = (props) => {
  const cfg = parseNeuralConfig(props.items.join(" "));
  const u = Math.min(props.box.h / 38, props.box.w / 34);
  const p = { ...props, u };
  switch (cfg.subject) {
    case "neuron": return <Neuron {...p} />;
    case "backprop": return <Backprop {...p} />;
    default: return <NetworkMLP {...p} />;
  }
};
